import { Comment, Avatar, Form, List, Input } from "antd";
import moment from "moment";
import { useDispatch } from "react-redux";
import React, { useState, useEffect } from "react";
import Button from "components/Button";
import avatar from "assets/avatar.png";
import { useTranslation } from "react-i18next";
import { decodeToken } from "utils";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import {
  commentApplicationAction,
  downloadAction,
  downloadDocumentAction,
} from "redux/actions/applicationAction";
import { useLocation } from "react-router-dom";
import { IconButton, Tooltip } from "@material-ui/core";
import "./style.css";


const { TextArea } = Input;


const CommentList = ({ comments, onDownload }) => {
  
  return (
    <List
      className="px-5"
      dataSource={comments}
      header={`${comments.length} ${comments.length > 1 ? "replies" : "reply"}`}
      itemLayout="horizontal"
      renderItem={(comment) => (
        <Comment
          author={
            <div>
              {comment.commentLevel === "applicant" ? "Applicant" : "Backend Officer"}
              {comment.organization && (
                <span className="text-uppercase">
                  &nbsp;@{comment.organization}
                </span>
              )}
            </div>
          }
          avatar={<Avatar src={avatar} alt="user avatar" />}
          content={
            <div>

              <p>{comment.adminComment || comment.applicantComment || 'No comment text available'}</p>
              {comment.documentName &&
                (Array.isArray(comment.documentName) ? (
                  comment.documentName.map((doc, index) => (
                    <div key={index}>
                      <small>
                        {doc}
                        <Tooltip title="Download">
                          <IconButton onClick={() => onDownload(comment.id)}>
                            <CloudDownloadIcon />
                          </IconButton>
                        </Tooltip>
                      </small>
                    </div>
                  ))
                ) : (
                  <small>
                    {comment.documentName}
                    <Tooltip title="Download">
                      <IconButton onClick={() => onDownload(comment.id)}>
                        <CloudDownloadIcon />
                      </IconButton>
                    </Tooltip>
                  </small>
                ))}
            </div>
          }
          datetime={moment(comment.createdAt).fromNow()}
        />
      )}
    />
  );
};


const Editor = ({ setAttachments, onChange, onSubmit, submitting, value }) => {
  const [buttonLabel, setButtonLabel] = useState("Upload files");
  const [selectedFiles, setSelectedFiles] = useState(null); 
  const fileInputRef = React.useRef(null);

  const handleUpload = (event) => {
    const uploadedFiles = Array.from(event.target.files);
    const validFiles = uploadedFiles.filter((file) => {
      const extension = file.name.match(/\.[0-9a-z]+$/i)?.[0];
      if (![".pdf", ".png", ".jpeg", ".jpg"].includes(extension)) {
        alert(`${file.name} has an unsupported file format.`);
        return false;
      }
      return true;
    });
    setSelectedFiles(validFiles?.length);


    setAttachments((prev) => [...prev, ...validFiles]);
  };


  return (
    <div className="card p-3">
      <Form.Item>
        <TextArea rows={4} onChange={onChange} value={value} />
      </Form.Item>
      <div className="d-flex flex-column">
      <input
          type="file"
          multiple
          className="p-3"
          ref={(el) => (fileInputRef.current = el)}
          style={{ display: "none" }}
          onChange={handleUpload}
          accept=".pdf,.png,.jpeg,.jpg"
        />
        <label htmlFor="fileUpload">
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderColor: "#767676",
            marginBottom: 10,
            border: "1px solid #767676",
            padding: '8px 20px', 
            backgroundColor: '#EFEFEF',
            color: '#000', 
            fontSize: '14px', 
            fontWeight: '500',
            cursor: 'pointer',  
            borderRadius: '4px', }} 
            onClick={() => fileInputRef.current.click()}> {buttonLabel}</div>
            
            {selectedFiles > 0 && (
              <div style={{ marginTop: 5, display: "flex", flexDirection: "row"}}>
                <strong>Selected Files:</strong>
                <ul>
                  {selectedFiles}
                </ul>
              </div>
            )}
        </label>
        <Button
          htmlType="submit"
          loading={submitting}
          onClick={onSubmit}
          type="primary"
        >
          Add Comment
        </Button>
      </div>
    </div>
  );
};


const CommentFormData = ({ applicationComments, showEditor,handleDownload }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const role = decodeToken()?.userRole?.RoleName;
  const location = useLocation();


  const [comments, setComments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [value, setValue] = useState("");
  const [attachments, setAttachments] = useState([]);


  useEffect(() => {
    if (applicationComments) {
      setComments(applicationComments);
    }
  }, [applicationComments]);



  const handleSubmit = () => {
    const id = location.pathname.split("/")[3];
  
    if (!value.trim() && attachments.length === 0) {
      alert("Please add a comment or upload a document.");
      return;
    }
  
    const formData = new FormData();
  
    if (value.trim()) {
      formData.append(
        role === "Applicant" ? "applicantComment" : "adminComment",
        value
      );
    }
  
    if (attachments.length > 0) {
      attachments.forEach((file) => {
        formData.append("documents", file);
      });
    }
  
    setSubmitting(true);
  
    commentApplicationAction(id, "admin", formData)(dispatch)
      .then((data) => {
        const ids = data?.documents?.length ? data.documents[data.documents.length - 1].id : null;
  
        const newComment = {
          id: ids || `temp-${Date.now()}`,
          adminComment: role !== "Applicant" ? value.trim() : null,
          applicantComment: role === "Applicant" ? value.trim() : null,
          documentName: attachments.length
            ? attachments.map((file) => file.name)
            : null,
          createdAt: new Date().toISOString(),
          commentLevel: role !== "Applicant" ? "backend-officer" : "applicant",
          organization: decodeToken()?.organizationName || null,
        };

        setComments((prev) => [newComment, ...prev]);
        setValue("");
        setAttachments([]);
      })
      .catch((error) => {
        console.error("Error submitting comment:", error);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };
  


  const handleChange = (e) => {
    setValue(e.target.value);
  };


  return (
    <div className="w-100">
      <div className="mx-2">
        {showEditor && (
          <Comment
            content={
              <Editor
                onChange={handleChange}
                onSubmit={handleSubmit}
                submitting={submitting}
                value={value}
                setAttachments={setAttachments}
              />
            }
          />
        )}
      </div>
      {comments?.length > 0 ? (
        <div className="cmt-section shadow">
          <CommentList
            comments={comments.map((comment) => ({
              ...comment,
              commentText:
                comment.commentLevel === "backend-officer"
                  ? comment.adminComment
                  : comment.applicantComment,
            }))}
            onDownload={handleDownload}
          />
        </div>
      ) : (
        <label className="text-muted px-2">{t("no-comment")}</label>
      )}
    </div>
  );
};


export default CommentFormData;


